import React, { useState, useEffect } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { toast } from 'react-toastify';
import {
  FaCheck, FaTimes, FaStar, FaShieldAlt, FaHeartbeat,
  FaArrowRight, FaUsers, FaCheckCircle,
  FaDumbbell, FaUserMd, FaPills, FaBolt, FaUtensils,
  FaChartLine, FaCalendarAlt, FaMobileAlt, FaComments,
  FaQuestionCircle, FaPlus, FaMinus, FaWhatsapp,
  FaFireAlt, FaLeaf, FaBrain
} from 'react-icons/fa';
import { useAuth } from '../context/AuthContext';
import { getPlans } from '../services/paymentAPI';
import SEO, { PageSEO } from '../components/SEO';

const corePlan = {
  id: 'plan_core',
  name: 'Fitnession Core',
  description: 'Complete lifestyle transformation — diet, workouts & 1:1 coaching, all in one place.',
  price: 2999,
  originalPrice: 3999,
  currency: 'INR',
  durationMonths: 1,
  programType: 'CORE',
  isPopular: true,
  discountPercentage: 25,
};

const features = [
  { icon: FaUserMd, title: 'Dedicated 1:1 Coach', desc: 'A certified coach assigned only to you — personalised, not generic.' },
  { icon: FaUtensils, title: 'Indian Meal Plans', desc: 'Custom plans using your local foods, budget & cooking style.' },
  { icon: FaComments, title: 'Expert Coaching Support', desc: 'Your coach available via dashboard — guidance, motivation & plan changes.' },
  { icon: FaChartLine, title: 'Progress Tracking', desc: 'Weekly check-ins, weight logs, measurements & photo comparisons.' },
  { icon: FaDumbbell, title: 'Custom Workouts', desc: 'Home or gym workouts adjusted as your fitness improves.' },
  { icon: FaCalendarAlt, title: 'Weekly Reviews', desc: 'Your coach reviews your week and adjusts plans accordingly.' },
  { icon: FaBrain, title: 'Habit & Mindset Work', desc: 'Build sustainable habits — not crash diets or extreme routines.' },
  { icon: FaMobileAlt, title: 'Dashboard Access', desc: 'Full app dashboard — plans, logs, chat, and progress in one place.' },
];

const comparisonRows = [
  { feature: 'Price / month', fitnession: '₹2,999', appCoach: '₹5,000+', dietPlan: '₹6,000+', nutritionist: '₹2,000–5,000 / session', apps: 'Free–₹1,500' },
  { feature: 'Dedicated 1:1 Coach', fitnession: true, appCoach: false, dietPlan: false, nutritionist: true, apps: false },
  { feature: 'Indian Meal Plans', fitnession: true, appCoach: 'Partial', dietPlan: true, nutritionist: 'Varies', apps: false },
  { feature: 'Expert Coaching Support', fitnession: true, appCoach: false, dietPlan: false, nutritionist: false, apps: false },
  { feature: 'Custom Workout Plans', fitnession: true, appCoach: 'Generic', dietPlan: false, nutritionist: false, apps: 'Generic' },
  { feature: 'Weekly Check-ins', fitnession: true, appCoach: false, dietPlan: 'Varies', nutritionist: false, apps: false },
  { feature: 'Progress Photo Tracking', fitnession: true, appCoach: false, dietPlan: false, nutritionist: false, apps: false },
  // { feature: 'Cancel Anytime', fitnession: true, appCoach: true, dietPlan: false, nutritionist: true, apps: true },
  { feature: 'Ongoing Support (Chat)', fitnession: true, appCoach: false, dietPlan: 'Partial', nutritionist: false, apps: false },
  { feature: 'Habit & Mindset Coaching', fitnession: true, appCoach: false, dietPlan: false, nutritionist: false, apps: false },
];

const faqs = [
  { q: 'What happens right after I subscribe?', a: 'You get instant access to your dashboard. Within 24 hours, your dedicated coach will reach out to schedule your first onboarding call and build your personalized plan.' },
  { q: 'Do I need a gym membership?', a: 'No. We build plans for home workouts, gym workouts, or a combination — based on what you have access to. Equipment, no equipment — we work with it.' },
  { q: 'What if my diet has restrictions (vegetarian, Jain, allergies)?', a: 'Your coach will build your meal plan around your exact preferences and restrictions. Indian vegetarian, vegan, gluten-free — whatever you need, it\'s covered.' },
  { q: 'How is this different from just using a diet app?', a: 'Apps give you generic templates. Fitnession gives you a real coach who knows your name, your lifestyle, your struggles, and adjusts your plan every week based on actual progress.' },
  // { q: 'Can I cancel anytime?', a: 'Yes. Cancel anytime from your dashboard — no questions asked. We offer a 30-day money-back guarantee if you\'re not satisfied after trying the program.' },
  { q: 'What payment methods are accepted?', a: 'All major credit/debit cards, UPI, net banking, and wallets via Razorpay. Your payment info is never stored on our servers.' },
];

const testimonials = [
  { name: 'Priya M.', city: 'Pune', result: 'Lost 11 kg in 3 months', text: 'I tried other coaching apps for 6 months and gave up. With Fitnession my coach actually checked in every week and changed my plan when I plateaued. Nothing generic here.', rating: 5 },
  { name: 'Rahul S.', city: 'Hyderabad', result: 'Lost 8 kg, gained energy', text: 'Finally a plan that uses South Indian food I actually eat. Not boiled chicken and salad. My coach built everything around my schedule and budget.', rating: 5 },
  { name: 'Ananya K.', city: 'Bengaluru', result: 'Down 2 dress sizes', text: 'My coach is available even at midnight when I\'m stressed and about to binge. And they review everything weekly. Best investment I\'ve made.', rating: 5 },
];

const CellValue = ({ value }) => {
  if (value === true) return <FaCheck className="text-green-500 mx-auto text-lg" />;
  if (value === false) return <FaTimes className="text-red-400 mx-auto text-lg" />;
  return <span className="text-gray-600 text-sm">{value}</span>;
};

const Pricing = () => {
  const navigate = useNavigate();
  const { isAuthenticated } = useAuth();
  const [plan, setPlan] = useState(corePlan);
  const [loading, setLoading] = useState(true);
  const [openFaq, setOpenFaq] = useState(null);

  useEffect(() => {
    fetchPlans();
  }, []);

  const fetchPlans = async () => {
    try {
      const data = await getPlans();
      const core = data.find(p => p.programType === 'CORE');
      if (core) setPlan(core);
    } catch {
      // fallback to default above
    } finally {
      setLoading(false);
    }
  };

  const handleSelectPlan = () => {
    if (!isAuthenticated) {
      toast.info('Please login to subscribe');
      navigate('/login', { state: { from: `/checkout/${plan.id}` } });
      return;
    }
    navigate(`/checkout/${plan.id}`);
  };

  const formatPrice = (p) =>
    new Intl.NumberFormat('en-IN', { style: 'currency', currency: 'INR', maximumFractionDigits: 0 }).format(p);

  return (
    <div className="min-h-screen bg-white">
      <SEO {...PageSEO.pricing} />

      {/* ─── HERO ─── */}
      <section className="bg-gradient-to-br from-primary-700 via-primary-800 to-primary-900 py-20 text-white text-center relative overflow-hidden">
        <div className="absolute inset-0 opacity-10" style={{ backgroundImage: 'radial-gradient(circle at 30% 50%, #4ade80 0%, transparent 50%), radial-gradient(circle at 70% 20%, #60a5fa 0%, transparent 50%)' }} />
        <div className="relative max-w-4xl mx-auto px-4">
          <div className="inline-flex items-center gap-2 bg-white/10 border border-white/20 rounded-full px-4 py-1.5 text-sm font-medium mb-6">
            <FaFireAlt className="text-amber-400" /> Launch offer — 25% off, limited time
          </div>
          <h1 className="text-4xl md:text-5xl font-extrabold mb-4 leading-tight">
            One plan. Everything you need.<br />
            <span className="text-primary-200">Nothing you don't.</span>
          </h1>
          <p className="text-xl text-primary-100 max-w-2xl mx-auto mb-8">
            1:1 coaching, Indian meal plans, expert support, and weekly progress reviews —
            all for less than a single gym session with a trainer.
          </p>
          {/* <div className="flex flex-wrap justify-center gap-6 text-white/80 text-sm">
            {['30-Day Money Back', 'Cancel Anytime', 'No Hidden Fees', 'Indian Food Plans'].map(t => (
              <span key={t} className="flex items-center gap-1.5"><FaCheckCircle className="text-green-400" />{t}</span>
            ))}
          </div> */}
        </div>
      </section>

      {/* ─── PRICING CARD ─── */}
      <section className="py-16 -mt-6">
        <div className="max-w-lg mx-auto px-4">
          {loading ? (
            <div className="flex justify-center py-16">
              <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary-600" />
            </div>
          ) : (
            <div className="bg-white rounded-3xl shadow-2xl border-2 border-primary-500 overflow-hidden">
              {/* Badge bar */}
              <div className="bg-gradient-to-r from-primary-600 to-success-600 py-3 text-center text-white font-bold text-sm tracking-wide">
                <FaStar className="inline mr-1.5 text-yellow-300" />
                MOST POPULAR — FITNESSION CORE
              </div>

              <div className="p-8">
                {/* Price */}
                <div className="text-center mb-8">
                  <p className="text-gray-500 text-sm mb-1">Monthly subscription</p>
                  <div className="flex items-end justify-center gap-2">
                    <span className="text-5xl font-extrabold text-gray-900">{formatPrice(plan.price)}</span>
                    <span className="text-gray-400 mb-1">/month</span>
                  </div>
                  <div className="flex items-center justify-center gap-3 mt-2">
                    <span className="text-gray-400 line-through text-lg">{formatPrice(plan.originalPrice)}</span>
                    <span className="bg-green-100 text-green-700 text-xs font-bold px-2.5 py-1 rounded-full">
                      SAVE ₹{(plan.originalPrice - plan.price).toLocaleString('en-IN')}/mo
                    </span>
                  </div>
                  <p className="text-gray-400 text-xs mt-2">That's just ₹100/day for a dedicated coach</p>
                </div>

                {/* CTA */}
                <button
                  onClick={handleSelectPlan}
                  className="w-full py-4 bg-gradient-to-r from-primary-600 to-success-600 hover:from-primary-700 hover:to-success-700 text-white font-bold text-lg rounded-2xl transition-all shadow-lg hover:shadow-xl flex items-center justify-center gap-2 mb-6"
                >
                  Start My Transformation
                  <FaArrowRight />
                </button>

                <p className="text-center text-xs text-gray-400 mb-8">
                  Secure checkout via Razorpay · No hidden fees
                </p>

                {/* Features list */}
                <div className="space-y-3">
                  {[
                    'Dedicated 1:1 certified coach (not shared)',
                    'Personalised Indian meal plan (rotates weekly)',
                    'Custom workout plan (home or gym)',
                    'Coach support via dashboard — anytime',
                    'Weekly progress review & plan adjustments',
                    'Weight, measurement & photo tracking',
                    'Habit & mindset coaching',
                    'WhatsApp support with your coach',
                    'Full app dashboard access',
                    // 'Cancel anytime, no lock-in',
                  ].map((f, i) => (
                    <div key={i} className="flex items-start gap-3">
                      <FaCheck className="text-green-500 mt-0.5 flex-shrink-0" />
                      <span className="text-gray-700 text-sm">{f}</span>
                    </div>
                  ))}
                </div>
              </div>
            </div>
          )}
        </div>
      </section>

      {/* ─── SOCIAL PROOF STRIP ─── */}
      <section className="bg-gray-50 py-10">
        <div className="max-w-5xl mx-auto px-4">
          <div className="grid grid-cols-2 md:grid-cols-4 gap-6 text-center">
            {[
              { num: '2,000+', label: 'Active Members' },
              { num: '94%', label: 'Hit Their Goal' },
              { num: '4.8★', label: 'Average Rating' },
              { num: '₹100/day', label: 'Cost Per Day' },
            ].map(({ num, label }) => (
              <div key={label}>
                <div className="text-3xl font-extrabold text-primary-600 mb-1">{num}</div>
                <div className="text-gray-500 text-sm">{label}</div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* ─── WHAT YOU GET ─── */}
      <section className="py-20">
        <div className="max-w-6xl mx-auto px-4">
          <div className="text-center mb-12">
            <h2 className="text-3xl md:text-4xl font-bold text-gray-900 mb-3">Everything included. Nothing watered down.</h2>
            <p className="text-gray-500 max-w-xl mx-auto">Every feature below is available to every Core member from day one.</p>
          </div>
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-6">
            {features.map(({ icon: Icon, title, desc }) => (
              <div key={title} className="bg-white border border-gray-100 rounded-2xl p-6 shadow-sm hover:shadow-md transition-shadow">
                <div className="w-12 h-12 bg-primary-50 rounded-xl flex items-center justify-center mb-4">
                  <Icon className="text-primary-600 text-xl" />
                </div>
                <h3 className="font-bold text-gray-900 mb-2">{title}</h3>
                <p className="text-gray-500 text-sm leading-relaxed">{desc}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* ─── COMPARISON TABLE ─── */}
      <section className="py-20 bg-gray-50">
        <div className="max-w-6xl mx-auto px-4">
          <div className="text-center mb-12">
            <h2 className="text-3xl md:text-4xl font-bold text-gray-900 mb-3">How we compare</h2>
            <p className="text-gray-500 max-w-2xl mx-auto">
              Before you decide, see exactly what you're getting versus the alternatives.
            </p>
          </div>

          <div className="overflow-x-auto rounded-2xl shadow-lg">
            <table className="w-full bg-white text-sm">
              <thead>
                <tr className="border-b border-gray-100">
                  <th className="text-left px-5 py-4 text-gray-500 font-medium w-40">Feature</th>
                  {/* Fitnession highlighted */}
                  <th className="px-4 py-4 bg-primary-50 border-x-2 border-primary-400">
                    <div className="font-extrabold text-primary-700 text-base">Fitnession Core</div>
                    <div className="text-primary-500 font-semibold text-lg">₹2,999/mo</div>
                  </th>
                  <th className="px-4 py-4 text-gray-700 font-semibold">
                    <div>App-Based</div>
                    <div className="text-gray-400 font-normal text-xs">Coach apps</div>
                  </th>
                  <th className="px-4 py-4 text-gray-700 font-semibold">
                    <div>Diet Plan</div>
                    <div className="text-gray-400 font-normal text-xs">Online services</div>
                  </th>
                  <th className="px-4 py-4 text-gray-700 font-semibold">
                    <div>Nutritionist</div>
                    <div className="text-gray-400 font-normal text-xs">In-person</div>
                  </th>
                  <th className="px-4 py-4 text-gray-700 font-semibold">
                    <div>Generic Apps</div>
                    <div className="text-gray-400 font-normal text-xs">Self-tracked</div>
                  </th>
                </tr>
              </thead>
              <tbody>
                {comparisonRows.map((row, i) => (
                  <tr key={i} className={`border-b border-gray-50 ${i % 2 === 0 ? 'bg-white' : 'bg-gray-50/50'}`}>
                    <td className="px-5 py-3.5 text-gray-700 font-medium">{row.feature}</td>
                    <td className="px-4 py-3.5 bg-primary-50 border-x-2 border-primary-400 text-center font-semibold">
                      <CellValue value={row.fitnession} />
                    </td>
                    <td className="px-4 py-3.5 text-center"><CellValue value={row.appCoach} /></td>
                    <td className="px-4 py-3.5 text-center"><CellValue value={row.dietPlan} /></td>
                    <td className="px-4 py-3.5 text-center"><CellValue value={row.nutritionist} /></td>
                    <td className="px-4 py-3.5 text-center"><CellValue value={row.apps} /></td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>

          <p className="text-center text-xs text-gray-400 mt-4">
            Based on typical market offerings across categories. Prices and features may vary.
          </p>
        </div>
      </section>

      {/* ─── TESTIMONIALS ─── */}
      <section className="py-20">
        <div className="max-w-6xl mx-auto px-4">
          <div className="text-center mb-12">
            <h2 className="text-3xl font-bold text-gray-900 mb-3">Real members. Real results.</h2>
            <p className="text-gray-500">We let the numbers do the talking.</p>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
            {testimonials.map(({ name, city, result, text, rating }) => (
              <div key={name} className="bg-white border border-gray-100 rounded-2xl p-7 shadow-sm hover:shadow-md transition-shadow">
                <div className="flex gap-1 mb-4">
                  {Array.from({ length: rating }).map((_, i) => <FaStar key={i} className="text-amber-400 text-sm" />)}
                </div>
                <p className="text-gray-600 text-sm leading-relaxed mb-5 italic">"{text}"</p>
                <div className="flex items-center gap-3">
                  <div className="w-10 h-10 rounded-full bg-primary-100 flex items-center justify-center font-bold text-primary-700 flex-shrink-0">
                    {name[0]}
                  </div>
                  <div>
                    <div className="font-semibold text-gray-900 text-sm">{name} · {city}</div>
                    <div className="text-green-600 text-xs font-medium">{result}</div>
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* ─── WHY NOT CHEAPER OPTIONS ─── */}
      <section className="py-16 bg-primary-700 text-white">
        <div className="max-w-5xl mx-auto px-4">
          <h2 className="text-2xl md:text-3xl font-bold text-center mb-10">Why free apps & generic plans keep failing you</h2>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
            {[
              { icon: FaMobileAlt, title: 'Generic apps can\'t see your life', desc: 'Diet apps don\'t know your job stress, family food, budget, or how much sleep you got. A real coach does.' },
              { icon: FaLeaf, title: '\"Eat salad\" isn\'t a plan', desc: 'Western-centric apps give you plans built for American bodies and American kitchens. Your food is different. Your plan should be too.' },
              { icon: FaChartLine, title: 'No one adjusts when you plateau', desc: 'Apps never react when the scale stops moving. Your Fitnession coach reviews every week and changes what isn\'t working.' },
            ].map(({ icon: Icon, title, desc }) => (
              <div key={title} className="bg-white/10 rounded-2xl p-6">
                <Icon className="text-primary-200 text-2xl mb-4" />
                <h3 className="font-bold mb-2">{title}</h3>
                <p className="text-primary-100 text-sm leading-relaxed">{desc}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* ─── FAQ ─── */}
      <section className="py-20">
        <div className="max-w-2xl mx-auto px-4">
          <div className="text-center mb-10">
            <h2 className="text-3xl font-bold text-gray-900 mb-3">Questions? We've got answers.</h2>
          </div>
          <div className="space-y-3">
            {faqs.map((faq, i) => (
              <div key={i} className="bg-white border border-gray-200 rounded-xl overflow-hidden">
                <button
                  onClick={() => setOpenFaq(openFaq === i ? null : i)}
                  className="w-full flex items-center justify-between p-5 text-left hover:bg-gray-50 transition-colors"
                >
                  <span className="font-semibold text-gray-900 pr-4">{faq.q}</span>
                  {openFaq === i ? <FaMinus className="text-primary-600 flex-shrink-0" /> : <FaPlus className="text-gray-400 flex-shrink-0" />}
                </button>
                {openFaq === i && (
                  <div className="px-5 pb-5 text-gray-600 text-sm leading-relaxed border-t border-gray-100 pt-4">
                    {faq.a}
                  </div>
                )}
              </div>
            ))}
          </div>
          <div className="text-center mt-8">
            <Link to="/faq" className="text-primary-600 hover:text-primary-700 font-medium inline-flex items-center gap-1.5 text-sm">
              View all FAQs <FaArrowRight className="text-xs" />
            </Link>
          </div>
        </div>
      </section>

      {/* ─── GLP-1 CALLOUT — hidden for now ─── */}
      {/* <section className="pb-12 px-4">
        <div className="max-w-3xl mx-auto">
          <div className="bg-gradient-to-r from-amber-50 to-orange-50 border border-amber-200 rounded-2xl p-6 flex flex-col sm:flex-row items-center justify-between gap-4">
            <div className="flex items-center gap-4">
              <div className="w-12 h-12 bg-amber-100 rounded-full flex items-center justify-center flex-shrink-0">
                <FaPills className="text-xl text-amber-600" />
              </div>
              <div>
                <h3 className="font-bold text-gray-900">Need medical-grade weight loss?</h3>
                <p className="text-sm text-gray-600">Doctor-supervised Semaglutide / Tirzepatide — ₹24,999/month on our GLP-1 program</p>
              </div>
            </div>
            {(() => {
              const isLocalhost = window.location.hostname === 'localhost' || window.location.hostname === '127.0.0.1';
              const glp1Url = isLocalhost ? '/?subdomain=glp1' : 'https://glp1.fitnession.fit';
              return (
                <a
                  href={glp1Url}
                  target={isLocalhost ? '_self' : '_blank'}
                  rel="noopener noreferrer"
                  className="flex items-center gap-2 bg-amber-500 hover:bg-amber-600 text-white px-6 py-3 rounded-xl font-semibold transition-colors whitespace-nowrap shadow-md"
                >
                  Check Eligibility <FaArrowRight />
                </a>
              );
            })()}
          </div>
        </div>
      </section> */}

      {/* ─── FINAL CTA ─── */}
      <section className="py-20 bg-gradient-to-br from-primary-700 to-success-700 text-white text-center">
        <div className="max-w-3xl mx-auto px-4">
          <h2 className="text-3xl md:text-4xl font-extrabold mb-4">
            Stop starting over.<br />Start making progress.
          </h2>
          <p className="text-xl text-white/80 mb-8">
            Join 2,000+ Indians who chose real coaching over another failed diet.
          </p>
          <button
            onClick={handleSelectPlan}
            className="inline-flex items-center gap-2 bg-white text-primary-700 font-bold text-lg px-10 py-4 rounded-2xl hover:bg-gray-100 transition-all shadow-xl"
          >
            Start for ₹2,999/month <FaArrowRight />
          </button>
          {/* <p className="text-white/50 text-sm mt-4">30-day money-back guarantee · Cancel anytime</p> */}

          <div className="mt-10 flex flex-wrap justify-center gap-8 text-white/70 text-sm">
            <span className="flex items-center gap-2"><FaShieldAlt className="text-green-400" /> 256-bit encrypted payments</span>
            <span className="flex items-center gap-2"><FaUsers className="text-blue-300" /> 2,000+ active members</span>
            <span className="flex items-center gap-2"><FaWhatsapp className="text-green-400" /> WhatsApp coach support</span>
          </div>
        </div>
      </section>
    </div>
  );
};

export default Pricing;
